import { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import {
  globalKnowledgeService,
  GlobalCategory,
  GlobalDocument,
  ListDocumentsParams,
} from '@/services/api/global-knowledge';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  LayoutGrid,
  LayoutList,
  FileText,
  ExternalLink,
  Calendar,
  Tag,
  ChevronLeft,
  ChevronRight,
  Library,
  TableIcon,
  Copy,
  Check,
  X,
  Trash2,
  Upload,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  List,
} from 'lucide-react';

const CATEGORY_COLORS: Record<string, string> = {
  // Document type categories
  'tax-rulings': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  'practice-statements': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  'legislation-guides': 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  'pre-filling-reports': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'forms-instructions': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'fact-sheets': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'service-guides': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  'benchmarks': 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  'general-ato': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  // Legacy topic categories (keep for backward compatibility)
  'division-7a': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  'smsf': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'trusts-section-100a': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  'cgt': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'public-rulings': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  'taxation-rulings': 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  'gst': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'rates-thresholds': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  'digital-services': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  'lodgment-reporting': 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  'super-professionals': 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  'tax-agent-services': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

const DOCUMENT_TYPES = [
  { slug: 'tax-rulings', name: 'Tax Rulings' },
  { slug: 'practice-statements', name: 'Practice Statements' },
  { slug: 'legislation-guides', name: 'Legislation Guides' },
  { slug: 'pre-filling-reports', name: 'Pre-filling Reports' },
  { slug: 'forms-instructions', name: 'Forms & Instructions' },
  { slug: 'fact-sheets', name: 'Fact Sheets' },
  { slug: 'service-guides', name: 'Service Guides' },
  { slug: 'benchmarks', name: 'ATO Benchmarks' },
  { slug: 'general-ato', name: 'General ATO' },
];

function CategoryBadge({ slug, name }: { slug?: string; name?: string }) {
  const colorClass = slug ? CATEGORY_COLORS[slug] || CATEGORY_COLORS['general-ato'] : CATEGORY_COLORS['general-ato'];
  const displayName = name || DOCUMENT_TYPES.find(dt => dt.slug === slug)?.name || slug || 'General';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {displayName}
    </span>
  );
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
}

function truncateText(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.substring(0, maxLen) + '...';
}

// Component to render chunk content with table support
function ChunkContent({ text }: { text: string }) {
  // Parse text and split into table/non-table sections
  const parts: Array<{ type: 'text' | 'table'; content: string }> = [];
  let remaining = text;

  while (remaining.length > 0) {
    const tableStart = remaining.indexOf('[TABLE]');
    if (tableStart === -1) {
      // No more tables, add remaining text
      if (remaining.trim()) {
        parts.push({ type: 'text', content: remaining });
      }
      break;
    }

    // Add text before table
    if (tableStart > 0) {
      const beforeTable = remaining.substring(0, tableStart);
      if (beforeTable.trim()) {
        parts.push({ type: 'text', content: beforeTable });
      }
    }

    // Find table end
    const tableEnd = remaining.indexOf('[/TABLE]', tableStart);
    if (tableEnd === -1) {
      // No closing tag, treat rest as text
      parts.push({ type: 'text', content: remaining.substring(tableStart) });
      break;
    }

    // Extract table content
    const tableContent = remaining.substring(tableStart + 7, tableEnd);
    parts.push({ type: 'table', content: tableContent });

    // Move past the table
    remaining = remaining.substring(tableEnd + 8);
  }

  if (parts.length === 0) {
    return (
      <p className="text-sm whitespace-pre-wrap break-words font-mono bg-muted/50 p-3 rounded">
        {text}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {parts.map((part, idx) => {
        if (part.type === 'text') {
          return (
            <p key={idx} className="text-sm whitespace-pre-wrap break-words font-mono bg-muted/50 p-3 rounded">
              {part.content.trim()}
            </p>
          );
        }

        // Parse table: rows separated by newlines, cells by pipes
        const rows = part.content.trim().split('\n').filter(row => row.trim());
        if (rows.length === 0) return null;

        return (
          <div key={idx} className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {rows.map((row, rowIdx) => {
                  const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
                  return (
                    <tr key={rowIdx} className={rowIdx === 0 ? 'bg-muted/70 font-medium' : ''}>
                      {cells.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="border border-border px-3 py-2 text-left"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Convert [TABLE]...[/TABLE] blocks to proper markdown tables.
 * The crawler format has pipe-delimited rows but no markdown header separator.
 * Also removes the duplicate flat text that follows each [/TABLE] block.
 */
function convertTablesToMarkdown(text: string): string {
  return text.replace(
    /\[TABLE\]([\s\S]*?)\[\/TABLE\](?:[\s\S]*?)(?=\n##|\n#[^#]|\n\n[A-Z]|\n\n[-*]\s|\n\n\d+\.\s|\[TABLE\]|$)/g,
    (_match, tableContent: string) => {
      const lines = tableContent
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);

      if (lines.length === 0) return '';

      // First non-separator line is the header
      const headerLine = lines.find((l: string) => !/^[\|\s\-]+$/.test(l));
      if (!headerLine) return '';

      const headers = headerLine.split('|').map((c: string) => c.trim()).filter(Boolean);
      if (headers.length === 0) return '';

      // Build markdown table
      let md = '\n| ' + headers.join(' | ') + ' |\n';
      md += '| ' + headers.map(() => '---').join(' | ') + ' |\n';

      for (const line of lines) {
        if (line === headerLine) continue;
        if (/^[\|\s\-]+$/.test(line)) continue;

        const cells = line.split('|').map((c: string) => c.trim()).filter(Boolean);
        if (cells.length === 0) continue;

        // Pad to match header count
        while (cells.length < headers.length) cells.push('');
        md += '| ' + cells.slice(0, headers.length).join(' | ') + ' |\n';
      }

      return md + '\n';
    }
  );
}

const PAGE_SIZE = 25;

// ===== Document Viewer Dialog =====

function DocumentViewerDialog({
  document,
  open,
  onClose,
}: {
  document: GlobalDocument | null;
  open: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const { data: contentData, isLoading: contentLoading } = useQuery({
    queryKey: ['global-doc-content', document?.id],
    queryFn: () => globalKnowledgeService.getDocumentContent(document!.id),
    enabled: open && !!document?.id,
    staleTime: 10 * 60 * 1000,
  });

  const rawContentText = contentData?.contentText || document?.contentText || '';
  const contentText = useMemo(() => convertTablesToMarkdown(rawContentText), [rawContentText]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(contentText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0 [&>button]:hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg font-semibold leading-tight">
                {document.mainTopic || document.originalFilename}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                {document.documentCategory && (
                  <CategoryBadge slug={document.documentCategory} />
                )}
                {document.publishDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Published {formatDate(document.publishDate)}
                  </span>
                )}
                {document.lastUpdatedDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Updated {formatDate(document.lastUpdatedDate)}
                  </span>
                )}
                {document.wordCount ? (
                  <span>{document.wordCount.toLocaleString()} words</span>
                ) : null}
                {document.hasTables && (
                  <span className="flex items-center gap-1">
                    <TableIcon className="h-3 w-3" />
                    {document.tableCount} table{document.tableCount !== 1 ? 's' : ''}
                  </span>
                )}
                {document.chunkCount ? (
                  <span>{document.chunkCount} chunks</span>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {document.sourceUrl && (
                <Button variant="outline" size="sm" asChild>
                  <a href={document.sourceUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    ATO
                  </a>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-4">
            {contentLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : contentText ? (
              <div className="prose prose-sm dark:prose-invert max-w-none
                prose-headings:scroll-mt-4
                prose-h2:text-lg prose-h2:font-semibold prose-h2:border-b prose-h2:pb-2 prose-h2:mb-3
                prose-h3:text-base prose-h3:font-medium
                prose-ul:my-1 prose-ol:my-1
                prose-li:my-0.5
                prose-p:my-2
                prose-table:border prose-table:border-border prose-table:rounded-md
                prose-th:bg-muted/50 prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:font-medium prose-th:text-xs prose-th:uppercase prose-th:tracking-wider
                prose-td:px-3 prose-td:py-2 prose-td:border-t prose-td:border-border prose-td:text-sm
                prose-pre:my-2 prose-pre:bg-muted prose-pre:text-foreground
                prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              ">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {contentText}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No content available for this document.</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ===== Upload Dialog =====

interface UploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'done' | 'error';
  progress: number;
  error?: string;
  documentId?: string;
}

function UploadDialog({
  open,
  onClose,
  onComplete,
}: {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);

  const { data: categoriesData } = useQuery({
    queryKey: ['global-kb-categories'],
    queryFn: () => globalKnowledgeService.listCategories(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const categories = categoriesData?.categories || [];

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const newFiles: UploadFile[] = Array.from(selectedFiles).map((file) => ({
      id: crypto.randomUUID(),
      file,
      status: 'pending' as const,
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const updateFile = useCallback((id: string, updates: Partial<UploadFile>) => {
    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, ...updates } : f));
  }, []);

  const uploadFiles = async () => {
    if (!selectedCategoryId || files.length === 0) return;
    setUploading(true);
    abortRef.current = false;

    const pendingFiles = files.filter((f) => f.status === 'pending' || f.status === 'error');

    for (const uf of pendingFiles) {
      if (abortRef.current) break;

      try {
        // Step 1: Get presigned upload URL
        updateFile(uf.id, { status: 'uploading', progress: 10, error: undefined });

        const contentType = uf.file.type || 'application/octet-stream';
        const urlResponse = await globalKnowledgeService.getUploadUrl(
          selectedCategoryId,
          uf.file.name,
          contentType,
          uf.file.size,
        );

        if (abortRef.current) break;
        updateFile(uf.id, { progress: 30, documentId: urlResponse.documentId });

        // Step 2: Upload file to S3
        await globalKnowledgeService.uploadFile(urlResponse.uploadUrl, uf.file, contentType);

        if (abortRef.current) break;
        updateFile(uf.id, { progress: 50 });

        // Step 3: Confirm upload
        await globalKnowledgeService.confirmUpload(urlResponse.documentId);

        if (abortRef.current) break;
        updateFile(uf.id, { status: 'processing', progress: 60 });

        // Step 4: Fire off processing (may 504 due to API Gateway 29s limit, but Lambda continues)
        try {
          await globalKnowledgeService.reprocessDocument(urlResponse.documentId);
        } catch {
          // 504 expected for large files - Lambda keeps running
        }

        if (abortRef.current) break;

        // Step 5: Poll for completion
        let indexed = false;
        for (let attempt = 0; attempt < 60; attempt++) {
          if (abortRef.current) break;
          await new Promise(resolve => setTimeout(resolve, 3000));
          try {
            const doc = await globalKnowledgeService.getDocument(urlResponse.documentId);
            if (doc.status === 'indexed') {
              indexed = true;
              break;
            }
            if (doc.status === 'failed') {
              throw new Error('Document processing failed');
            }
          } catch (pollErr: any) {
            if (pollErr.message === 'Document processing failed') throw pollErr;
          }
        }

        if (indexed) {
          updateFile(uf.id, { status: 'done', progress: 100 });
        } else {
          updateFile(uf.id, { status: 'error', progress: 100, error: 'Processing timeout' });
        }
      } catch (err: any) {
        const message = err?.response?.data?.error || err?.message || 'Upload failed';
        updateFile(uf.id, { status: 'error', progress: 100, error: message });
      }
    }

    setUploading(false);
    onComplete();
  };

  const stopUpload = () => {
    abortRef.current = true;
  };

  const clearCompleted = () => {
    setFiles((prev) => prev.filter((f) => f.status !== 'done'));
  };

  const handleClose = () => {
    if (uploading) return;
    setFiles([]);
    setSelectedCategoryId('');
    onClose();
  };

  const pendingCount = files.filter((f) => f.status === 'pending' || f.status === 'error').length;
  const doneCount = files.filter((f) => f.status === 'done').length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 [&>button]:hidden">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold">Upload Documents</DialogTitle>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleClose} disabled={uploading}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {/* Category selector */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Category</Label>
            <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId} disabled={uploading}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a category..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat: GlobalCategory) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Drop zone */}
          <div
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">Drop files here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">
              Supports PDF, TXT, DOCX, CSV, XLSX and more
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  {files.length} file{files.length !== 1 ? 's' : ''}
                  {doneCount > 0 && ` (${doneCount} completed)`}
                </p>
                {doneCount > 0 && !uploading && (
                  <Button variant="ghost" size="sm" onClick={clearCompleted}>
                    Clear completed
                  </Button>
                )}
              </div>

              <div className="border rounded-lg divide-y max-h-[300px] overflow-auto">
                {files.map((uf) => (
                  <div key={uf.id} className="flex items-center gap-3 px-3 py-2">
                    {/* Status icon */}
                    <div className="shrink-0">
                      {uf.status === 'pending' && <FileText className="h-4 w-4 text-muted-foreground" />}
                      {uf.status === 'uploading' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
                      {uf.status === 'processing' && <Loader2 className="h-4 w-4 text-orange-500 animate-spin" />}
                      {uf.status === 'done' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                      {uf.status === 'error' && <XCircle className="h-4 w-4 text-destructive" />}
                    </div>

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{uf.file.name}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {(uf.file.size / 1024).toFixed(0)} KB
                        </span>
                        {uf.status === 'uploading' && (
                          <span className="text-xs text-blue-500">Uploading...</span>
                        )}
                        {uf.status === 'processing' && (
                          <span className="text-xs text-orange-500">Processing...</span>
                        )}
                        {uf.error && (
                          <span className="text-xs text-destructive truncate">{uf.error}</span>
                        )}
                      </div>
                      {(uf.status === 'uploading' || uf.status === 'processing') && (
                        <Progress value={uf.progress} className="h-1 mt-1" />
                      )}
                    </div>

                    {/* Remove button */}
                    {(uf.status === 'pending' || uf.status === 'error') && !uploading && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={() => removeFile(uf.id)}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between bg-muted/30">
          <div className="text-xs text-muted-foreground">
            {!selectedCategoryId && files.length > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertCircle className="h-3 w-3" /> Select a category first
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {uploading ? (
              <Button variant="destructive" size="sm" onClick={stopUpload}>
                Stop
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={uploadFiles}
                  disabled={!selectedCategoryId || pendingCount === 0}
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  Upload {pendingCount > 0 ? `${pendingCount} file${pendingCount !== 1 ? 's' : ''}` : ''}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Main Page =====

export function GlobalKnowledgeBasePage() {
  const { isSuperAdmin } = useAuthStore();
  const superAdmin = isSuperAdmin();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<ListDocumentsParams['sortBy']>('publishDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [page, setPage] = useState(0);
  const [viewerDoc, setViewerDoc] = useState<GlobalDocument | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState<GlobalDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [viewChunksDialogOpen, setViewChunksDialogOpen] = useState(false);
  const [viewChunksDoc, setViewChunksDoc] = useState<GlobalDocument | null>(null);
  const [chunksData, setChunksData] = useState<{
    documentId: string;
    filename: string;
    mainTopic?: string;
    totalChunks: number;
    chunks: Array<{ index: number; text: string; tokens?: number }>;
  } | null>(null);
  const [loadingChunks, setLoadingChunks] = useState(false);

  // Debounce search
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 400);
    setSearchTimeout(timeout);
  };

  // Fetch documents
  const queryParams = useMemo<ListDocumentsParams>(() => ({
    ...(selectedCategory !== 'all' ? { documentCategory: selectedCategory } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    sortBy: sortBy || 'publishDate',
    sortOrder,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    status: 'indexed',
  }), [selectedCategory, debouncedSearch, sortBy, sortOrder, page]);

  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ['global-kb-documents', queryParams],
    queryFn: () => globalKnowledgeService.listDocuments(queryParams),
    staleTime: 2 * 60 * 1000,
  });

  const documents = docsData?.documents || [];
  const totalDocs = docsData?.total || 0;
  const totalPages = Math.ceil(totalDocs / PAGE_SIZE);

  // Stats
  const statsText = totalDocs > 0 ? `${totalDocs} document${totalDocs !== 1 ? 's' : ''}` : '';

  const openViewer = (doc: GlobalDocument) => {
    setViewerDoc(doc);
    setViewerOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await globalKnowledgeService.deleteDocument(deleteDoc.id);
      toast.success('Document deleted', {
        description: `"${deleteDoc.mainTopic || deleteDoc.originalFilename}" has been removed.`,
      });
      queryClient.invalidateQueries({ queryKey: ['global-kb-documents'] });
    } catch (err: any) {
      toast.error('Failed to delete document', {
        description: err?.response?.data?.error || err.message || 'Unknown error',
      });
    } finally {
      setDeleting(false);
      setDeleteDoc(null);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      // Pass category if filtered, otherwise delete all
      const categoryId = selectedCategory !== 'all' ? selectedCategory : undefined;
      const result = await globalKnowledgeService.deleteAllDocuments(categoryId);
      toast.success(`Deleted ${result.deletedCount} documents`, {
        description: categoryId ? `All documents in selected category removed.` : 'All ATO documents removed.',
      });
      queryClient.invalidateQueries({ queryKey: ['global-kb-documents'] });
      queryClient.invalidateQueries({ queryKey: ['global-categories'] });
    } catch (err: any) {
      toast.error('Failed to delete documents', {
        description: err?.response?.data?.error || err.message || 'Unknown error',
      });
    } finally {
      setDeletingAll(false);
      setDeleteAllOpen(false);
    }
  };

  const handleViewChunks = async (doc: GlobalDocument) => {
    setViewChunksDoc(doc);
    setViewChunksDialogOpen(true);
    setLoadingChunks(true);
    setChunksData(null);
    try {
      const data = await globalKnowledgeService.getDocumentChunks(doc.id);
      setChunksData(data);
    } catch (error) {
      console.error('Failed to fetch chunks:', error);
    } finally {
      setLoadingChunks(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 px-6 py-4 border-b bg-background">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Library className="h-5 w-5" />
              ATO Knowledge Base
            </h1>
            {statsText && (
              <p className="text-sm text-muted-foreground mt-0.5">{statsText}</p>
            )}
          </div>
          {superAdmin && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setUploadOpen(true)}>
                <Upload className="h-4 w-4 mr-1.5" />
                Upload
              </Button>
              {totalDocs > 0 && (
                <Button size="sm" variant="destructive" onClick={() => setDeleteAllOpen(true)}>
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Delete All
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          {/* Document type filter */}
          <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setPage(0); }}>
            <SelectTrigger className="w-[210px] h-9">
              <Tag className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="All Document Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Document Types</SelectItem>
              {DOCUMENT_TYPES.map((dt) => (
                <SelectItem key={dt.slug} value={dt.slug}>
                  {dt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort */}
          <Select
            value={`${sortBy}-${sortOrder}`}
            onValueChange={(v) => {
              const [sb, so] = v.split('-') as [string, 'asc' | 'desc'];
              setSortBy(sb as ListDocumentsParams['sortBy']);
              setSortOrder(so);
              setPage(0);
            }}
          >
            <SelectTrigger className="w-[190px] h-9">
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="publishDate-desc">Newest Published</SelectItem>
              <SelectItem value="publishDate-asc">Oldest Published</SelectItem>
              <SelectItem value="lastUpdatedDate-desc">Recently Updated</SelectItem>
              <SelectItem value="title-asc">Title A-Z</SelectItem>
              <SelectItem value="title-desc">Title Z-A</SelectItem>
              <SelectItem value="documentCategory-asc">Document Type A-Z</SelectItem>
              <SelectItem value="createdAt-desc">Recently Indexed</SelectItem>
            </SelectContent>
          </Select>

          {/* View toggle */}
          <div className="flex items-center border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-9 px-2.5 rounded-r-none"
              onClick={() => setViewMode('table')}
            >
              <LayoutList className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-9 px-2.5 rounded-l-none"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {docsLoading ? (
          <LoadingSkeleton viewMode={viewMode} />
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <FileText className="h-16 w-16 mb-4 opacity-20" />
            <p className="text-lg font-medium">No documents found</p>
            <p className="text-sm mt-1">
              {debouncedSearch || selectedCategory !== 'all'
                ? 'Try adjusting your filters or search query'
                : 'No ATO documents have been indexed yet'}
            </p>
          </div>
        ) : viewMode === 'table' ? (
          <DocumentTable documents={documents} onViewDocument={openViewer} onDeleteDocument={superAdmin ? setDeleteDoc : undefined} onViewChunks={superAdmin ? handleViewChunks : undefined} />
        ) : (
          <DocumentGrid documents={documents} onViewDocument={openViewer} onDeleteDocument={superAdmin ? setDeleteDoc : undefined} onViewChunks={superAdmin ? handleViewChunks : undefined} />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalDocs)} of {totalDocs}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Document Viewer Dialog */}
      <DocumentViewerDialog
        document={viewerDoc}
        open={viewerOpen}
        onClose={() => { setViewerOpen(false); setViewerDoc(null); }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDoc} onOpenChange={(open) => !open && setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteDoc?.mainTopic || deleteDoc?.originalFilename}" including all embedding chunks and the S3 file. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Confirmation Dialog */}
      <AlertDialog open={deleteAllOpen} onOpenChange={(open) => !open && setDeleteAllOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Documents</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>ALL {totalDocs} documents</strong>
              {selectedCategory !== 'all' ? ` in the selected category` : ''}?
              <span className="block mt-2 text-destructive font-medium">
                This will permanently delete all files from storage and remove all vector embeddings.
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAll}
              disabled={deletingAll}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingAll ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete All Documents
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Dialog */}
      {superAdmin && (
        <UploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onComplete={() => queryClient.invalidateQueries({ queryKey: ['global-kb-documents'] })}
        />
      )}

      {/* View Chunks Dialog */}
      <Dialog open={viewChunksDialogOpen} onOpenChange={setViewChunksDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Document Chunks
            </DialogTitle>
            {viewChunksDoc && (
              <p className="text-sm text-muted-foreground truncate">
                {viewChunksDoc.mainTopic || viewChunksDoc.originalFilename}
                {chunksData && ` — ${chunksData.totalChunks} chunks`}
              </p>
            )}
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] px-4 py-4">
            {loadingChunks ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : chunksData && chunksData.chunks.length > 0 ? (
              <div className="space-y-4">
                {chunksData.chunks.map((chunk) => (
                  <div key={chunk.index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs">
                        Chunk {chunk.index + 1}
                      </Badge>
                      {chunk.tokens && (
                        <span className="text-xs text-muted-foreground">
                          {chunk.tokens} tokens
                        </span>
                      )}
                    </div>
                    <ChunkContent text={chunk.text} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No chunks found for this document</p>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setViewChunksDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Table View =====

function DocumentTable({
  documents,
  onViewDocument,
  onDeleteDocument,
  onViewChunks,
}: {
  documents: GlobalDocument[];
  onViewDocument: (doc: GlobalDocument) => void;
  onDeleteDocument?: (doc: GlobalDocument) => void;
  onViewChunks?: (doc: GlobalDocument) => void;
}) {
  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40%]">Document</TableHead>
            <TableHead className="w-[15%]">Document Type</TableHead>
            <TableHead className="w-[12%]">Published</TableHead>
            <TableHead className="w-[12%]">Updated</TableHead>
            <TableHead className="w-[8%] text-center">Status</TableHead>
            <TableHead className="w-[13%] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc) => (
            <TableRow
              key={doc.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onViewDocument(doc)}
            >
              <TableCell>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {doc.mainTopic || doc.originalFilename}
                  </p>
                  {doc.sourceUrl && (() => {
                    const path = doc.sourceUrl.replace('https://www.ato.gov.au', '');
                    return (
                      <p className="text-xs text-muted-foreground truncate mt-0.5" title={path}>
                        {path.length > 48 ? path.substring(0, 48) + '...' : path}
                      </p>
                    );
                  })()}
                </div>
              </TableCell>
              <TableCell>
                <CategoryBadge slug={doc.documentCategory} />
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(doc.publishDate)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(doc.lastUpdatedDate)}
              </TableCell>
              <TableCell className="text-center">
                <Badge
                  variant={doc.status === 'indexed' ? 'default' : doc.status === 'failed' ? 'destructive' : 'secondary'}
                  className="text-[10px]"
                >
                  {doc.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {doc.hasTables && (
                    <Badge variant="outline" className="text-[10px] gap-0.5">
                      <TableIcon className="h-2.5 w-2.5" />
                      {doc.tableCount}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={(e) => { e.stopPropagation(); onViewDocument(doc); }}
                  >
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  {onViewChunks && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewChunks(doc);
                      }}
                      title="View Chunks"
                    >
                      <List className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {doc.sourceUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(doc.sourceUrl, '_blank');
                      }}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {onDeleteDocument && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteDocument(doc);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ===== Grid / Card View =====

function DocumentGrid({
  documents,
  onViewDocument,
  onDeleteDocument,
  onViewChunks,
}: {
  documents: GlobalDocument[];
  onViewDocument: (doc: GlobalDocument) => void;
  onDeleteDocument?: (doc: GlobalDocument) => void;
  onViewChunks?: (doc: GlobalDocument) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {documents.map((doc) => (
        <Card
          key={doc.id}
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onViewDocument(doc)}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <CategoryBadge slug={doc.documentCategory} />
              <Badge
                variant={doc.status === 'indexed' ? 'default' : doc.status === 'failed' ? 'destructive' : 'secondary'}
                className="text-[10px] shrink-0"
              >
                {doc.status}
              </Badge>
            </div>

            <h3 className="font-medium text-sm line-clamp-2 mb-1">
              {doc.mainTopic || doc.originalFilename}
            </h3>

            {doc.sourceUrl && (() => {
              const path = doc.sourceUrl.replace('https://www.ato.gov.au', '');
              return (
                <p className="text-xs text-muted-foreground truncate mb-2" title={path}>
                  {path.length > 48 ? path.substring(0, 48) + '...' : path}
                </p>
              );
            })()}

            {doc.subTopics && doc.subTopics.length > 0 && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                {doc.subTopics.join(', ')}
              </p>
            )}

            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
              <div className="flex items-center gap-3">
                {doc.publishDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(doc.publishDate)}
                  </span>
                )}
                {doc.wordCount ? (
                  <span>{doc.wordCount.toLocaleString()} words</span>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {doc.hasTables && (
                  <span className="flex items-center gap-0.5">
                    <TableIcon className="h-3 w-3" />
                    {doc.tableCount}
                  </span>
                )}
                {onViewChunks && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewChunks(doc);
                    }}
                    title="View Chunks"
                  >
                    <List className="h-3 w-3" />
                  </Button>
                )}
                {doc.sourceUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(doc.sourceUrl, '_blank');
                    }}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
                {onDeleteDocument && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteDocument(doc);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ===== Loading Skeleton =====

function LoadingSkeleton({ viewMode }: { viewMode: 'table' | 'grid' }) {
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4 space-y-3">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <div className="flex justify-between pt-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <div className="p-3 space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
